<script setup lang="ts">
/**
 * Design-Editor (editorConfig.mode = 'design'): Dokument laden/anlegen, Bühne + Panels,
 * Produkt/Format/Optionen über die bestehende Konfigurator-Sidebar (controlled), Warenkorb.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { canvasFromGeometry, documentHash, normalizeGeometry, type UploadRef } from '@pfe/design-renderer'
import { useDesignStore } from '../../store/useDesignStore'
import { useProjectStore } from '../../store/useProjectStore'
import { useCartStore } from '../../store/cart'
import { useToast } from '../../composables/useToast'
import { fetchEditorCatalogBootstrap, type EditorCatalogBootstrapResponse } from '../../services/editorCatalogApi'
import { listFonts, listSheets, requestDesignPreview, requestDesignProof, type DesignFontSummary, type DesignTemplateSummary } from '../../services/designApi'
import { snapshotSurface } from '../../design/snapshot'
import ProofDialog from '../design/ProofDialog.vue'
import { subscribeToUploadEvents } from '../../services/uploadEvents'
import { getWidgetConfig } from '../../runtime/widgetConfig'
import { resolveEditorConfig, sheetForSelection } from '../../utils/editorConfigResolver'
import { ensureFontsLoaded } from '../../design/fonts/fontLoader'
import DesignEditorShell from '../design/DesignEditorShell.vue'
import Stage from '../design/Stage.vue'
import ImagesPanel from '../design/panels/ImagesPanel.vue'
import type { StageDropTarget } from '../design/Stage.vue'
import { uploadRefOf, useDesignUploads } from '../../design/useDesignUploads'
import TextPanel from '../design/panels/TextPanel.vue'
import LayoutsPanel from '../design/panels/LayoutsPanel.vue'
import TemplatesPanel from '../design/panels/TemplatesPanel.vue'
import BackgroundPanel from '../design/panels/BackgroundPanel.vue'
import StickersPanel from '../design/panels/StickersPanel.vue'
import MyDesignsPanel from '../design/panels/MyDesignsPanel.vue'
import Sidebar from '../editor/configurator/sidebar.vue'
import { ArrowLeft, Check, Image as ImageIcon, Pencil, CloudOff, Eye, LoaderCircle, Redo2, Scissors, ShoppingCart, TriangleAlert, Undo2 } from 'lucide-vue-next'

const route = useRoute()
const router = useRouter()
const store = useDesignStore()
const project = useProjectStore()
const cart = useCartStore()
const toast = useToast()
const cfg = getWidgetConfig() as any

const categoryId = computed(() => Number(route.query.categoryId || route.query.category || 0) || 0)
const productId = ref(Number(route.query.productId || route.query.product || 0) || 0)
const designId = computed(() => String(route.query.designId || '').trim() || null)

const bootstrap = ref<EditorCatalogBootstrapResponse | null>(null)
const fonts = ref<DesignFontSummary[]>([])
const loading = ref(true)
const loadError = ref('')
const guides = ref<'full' | 'cut-only' | 'off'>('full')
const productView = ref(false)
const quantity = ref(1)
const preset = ref<{ size: any | null; options: any[]; quantity: number; grayscale: boolean }>({ size: null, options: [], quantity: 1, grayscale: false })
const shellRef = ref<any>(null)
const replaceTargetId = ref<string | null>(null)
const designUploads = useDesignUploads()
/** Auf die Bühne gezogene Dateien: Datensatz-ID -> Ziel, eingesetzt sobald der Upload fertig ist. */
const pendingDrops = new Map<string, StageDropTarget>()
let designUploadsStarted = false
const addingToCart = ref(false)
const priceLabel = ref('')
const overviewHref = computed(() => String(cfg?.overviewUrl || '/').trim() || '/')
const cartHref = computed(() => String(cfg?.cartUrl || '/checkout/cart').trim())
const saveLabel = computed(() => ({ saving: 'Speichert…', saved: 'Gespeichert', conflict: 'Konflikt', offline: 'Offline – lokal gesichert', error: 'Fehler beim Speichern' } as Record<string, string>)[store.saveState] || '')
const guidesTitle = computed(() => guides.value === 'full' ? 'Produktionsansicht: Beschnitt + Sicherheitsbereich (Klick: nur Schnittkante)' : guides.value === 'cut-only' ? 'Nur Schnittkante (Klick: aus)' : 'Produktionsansicht aus (Klick: an)')
const proof = ref<{ open: boolean; status: 'loading' | 'ready' | 'fallback' | 'error'; imageUrl: string; proofHash: string; warnings: any[]; error: string }>({ open: false, status: 'loading', imageUrl: '', proofHash: '', warnings: [], error: '' })
let proofResolve: ((ok: boolean) => void) | null = null
let stopMercure: (() => void) | null = null

const product = computed(() => bootstrap.value?.products?.[String(productId.value)] ?? null)
const editorConfig = computed(() => resolveEditorConfig({ product: product.value as any, categoryId: categoryId.value }))
/** Bogen-Modus (Fotosticker, Passbilder …): feste Plätze, nur Fotos einsetzen/tauschen/zuschneiden */
const sheetMode = computed(() => editorConfig.value.sheets.length > 0)
const features = computed(() => sheetMode.value
  ? { ...editorConfig.value.features, text: false, stickers: false, templates: false, collage: false, backgrounds: false, filters: false, shapes: false, designs: true }
  : { ...editorConfig.value.features, designs: true })
const productTitles = computed<Record<string, string>>(() => {
  const out: Record<string, string> = {}
  for (const [id, p] of Object.entries(bootstrap.value?.products ?? {})) out[id] = String((p as any)?.title || '')
  return out
})
const palette = computed(() => ['#0F172A', '#FFFFFF', '#E30B17', '#1D4ED8', '#059669', '#D97706', '#7C3AED', '#64748B'])
const sizeOptions = computed(() => {
  const g = (product.value?.configurator?.groups ?? []).find((x: any) => x.isSize)
  return Array.isArray(g?.sizeOptions) ? g.sizeOptions : []
})
const currentSizeId = computed(() => store.activeMeta?.sizeId ?? null)

async function boot() {
  loading.value = true
  loadError.value = ''
  try {
    bootstrap.value = await fetchEditorCatalogBootstrap({ editorBaseUrl: cfg.editorBaseUrl })
    const code = await project.ensureProject(String(route.query.projectCode || '') || null)
    if (!code) throw new Error('Kein Projekt')
    const sizeOpt = sizeOptions.value.find((o: any) => o.isDefault) ?? sizeOptions.value[0] ?? null
    await store.load({
      projectCode: code,
      designId: designId.value,
      productId: productId.value,
      categoryId: categoryId.value,
      sizeId: sizeOpt ? Number(sizeOpt.id) : null,
      selectedVariantIds: sizeOpt?.variantId ? [Number(sizeOpt.variantId)] : [],
      sides: editorConfig.value.canvas.sides === 2 ? ['front', 'back'] : ['front'],
    })
    if (store.activeMeta) {
      productId.value = store.activeMeta.productId || productId.value
      if (!designId.value) void router.replace({ query: { ...route.query, designId: store.activeMeta.id } })
    }
    guides.value = editorConfig.value.canvas.showGuides ? 'full' : 'off'
    const fontRes = await listFonts({ editorBaseUrl: cfg.editorBaseUrl, productId: productId.value, categoryId: categoryId.value }).catch(() => ({ items: [] as DesignFontSummary[] }))
    fonts.value = fontRes.items
    store.availableFontIds = fonts.value.map((f) => f.id)
    await loadUsedFonts()
    if (!designUploadsStarted) designUploadsStarted = true
    else designUploads.stop()
    designUploads.start({ projectCode: code, productId: productId.value, categoryId: categoryId.value, product: product.value })
    subscribe(code)
    await loadSheets()
    ensureSheet()
  } catch (e: any) {
    loadError.value = e?.error || e?.message || 'Der Editor konnte nicht geladen werden.'
  } finally {
    loading.value = false
  }
}

async function loadUsedFonts() {
  const ids = new Set<string>()
  for (const d of [store.doc, store.sibling]) for (const l of d?.layers ?? []) if (l.type === 'text' && l.font.familyId) ids.add(l.font.familyId)
  await ensureFontsLoaded(fonts.value, ids)
}
watch(() => store.activeDoc?.layers.map((l) => (l.type === 'text' ? l.font.familyId : '')).join(','), () => { void loadUsedFonts() })

function subscribe(code: string) {
  stopMercure?.()
  stopMercure = subscribeToUploadEvents({
    editorBaseUrl: cfg.editorBaseUrl,
    mercureHubUrl: cfg.mercureHubUrl,
    projectCode: code,
    categoryId: categoryId.value,
    onUpload: () => { void designUploads.reload() },
    onDeleted: () => { void designUploads.reload() },
    onRenderEvent: (ev) => {
      if (ev.event === 'render:done' && ev.kind === 'preview') void cart.fetchCart().catch(() => {})
      if (ev.event === 'render:failed') toast.error('Vorschau konnte nicht erzeugt werden.')
    },
  })
}

// ---------- Bögen ----------
const sheets = ref<DesignTemplateSummary[]>([])
const activeSheet = computed(() => sheets.value.find((t) => t.id === store.activeDoc?.meta.sheet?.templateId) ?? null)
const sheetSlots = computed(() => (store.activeDoc?.layers ?? []).filter((l) => l.type === 'image'))
const sheetFilled = computed(() => sheetSlots.value.filter((l) => l.type === 'image' && l.source.kind !== 'placeholder').length)
async function loadSheets() {
  if (!sheetMode.value) { sheets.value = []; return }
  try {
    const res = await listSheets({ editorBaseUrl: cfg.editorBaseUrl, productId: productId.value, categoryId: categoryId.value })
    sheets.value = res.items.filter((t) => !!t.document)
  } catch {
    sheets.value = []
    toast.error('Die Bögen konnten nicht geladen werden.')
  }
}
/** Passenden Bogen für Größe + Optionen setzen (nur wenn er sich ändert) */
function ensureSheet() {
  if (!sheetMode.value || !sheets.value.length || !store.activeDoc || store.templateMode) return
  const wanted = sheetForSelection(editorConfig.value.sheets, currentSizeId.value, preset.value.options) ?? sheets.value[0]!.id
  const sheet = sheets.value.find((t) => t.id === wanted) ?? sheets.value[0]!
  if (store.activeDoc.meta.sheet?.templateId === sheet.id) return
  store.applySheet({ id: sheet.id, version: sheet.version, name: sheet.name, document: sheet.document! })
}
watch(() => [sheets.value, currentSizeId.value, preset.value.options.map((o: any) => `${o?.variantId}:${o?.value}`).join('|'), !!store.activeDoc] as const, ensureSheet)
const selectedUpload = computed(() => {
  const l = store.selectedLayer
  return l?.type === 'image' && l.source.kind === 'upload' ? { uploadId: l.source.uploadId, url: l.source.url, width: l.source.width, height: l.source.height } : null
})
function fillEmptySlotsWithSelected() {
  if (selectedUpload.value) store.fillSlots(selectedUpload.value, true)
}

// ---------- Bilder ----------
function onPick(upload: UploadRef) {
  const target = replaceTargetId.value || (store.selectedLayer?.type === 'image' ? store.selectedLayer.id : null)
  if (target) {
    store.setImageSource(target, upload)
    replaceTargetId.value = null
    return
  }
  const emptySlot = store.activeDoc?.layers.find((l) => l.type === 'image' && l.source.kind === 'placeholder')
  if (emptySlot) { store.setImageSource(emptySlot.id, upload); return }
  if (sheetMode.value) { toast.info('Alle Plätze sind belegt. Platz anklicken und dann ein Foto wählen, um es zu ersetzen.'); return }
  store.addImageLayer(upload)
}
function onDropUpload(upload: UploadRef, target: StageDropTarget) {
  placeDropped(upload, target)
}
function placeDropped(upload: UploadRef, target: StageDropTarget) {
  const layer = target.layerId ? store.activeDoc?.layers.find((l) => l.id === target.layerId) : null
  if (layer?.type === 'image') {
    store.setImageSource(layer.id, upload)
    store.select([layer.id])
    return
  }
  if (sheetMode.value) { onPick(upload); return }
  store.addImageLayer(upload, { xMm: target.xMm, yMm: target.yMm })
}
function openImagesTool() {
  shellRef.value?.openTool?.('images')
}
/** blob:-Vorschau je Upload-ID (lokal erzeugt), bis der Server-Upload fertig ist. */
const localPreviewUrls = new Map<string, string>()
function localPreviewRef(id: string): UploadRef | null {
  const rec = designUploads.records.value.find((r) => r.id === id) as any
  const blob: Blob | undefined = rec?.editorBlob || rec?.workingBlob || rec?.thumbBlob
  if (!rec || !blob) return null
  let url = localPreviewUrls.get(id)
  if (!url) { url = URL.createObjectURL(blob); localPreviewUrls.set(id, url) }
  return { uploadId: id, url, width: rec.width || rec.editorWidth, height: rec.height || rec.editorHeight }
}
onBeforeUnmount(() => { for (const url of localPreviewUrls.values()) URL.revokeObjectURL(url); localPreviewUrls.clear() })

async function onDropFiles(files: File[], target: StageDropTarget) {
  openImagesTool()
  const ids = await designUploads.importFiles(files)
  if (!ids.length) { toast.error('Bitte Bilddateien (JPG, PNG, HEIC …) verwenden.'); return }
  // Mehrere Dateien auf einen Slot: erste in den Slot, Rest als neue Ebenen versetzt daneben.
  // Sofort mit lokaler Vorschau einsetzen; die Server-URL tauscht der Store, sobald der Upload fertig ist.
  ids.forEach((id, i) => {
    const at = i === 0 ? target : { layerId: null, xMm: target.xMm + i * 8, yMm: target.yMm + i * 8 }
    const ready = designUploads.readyIds.value.has(id)
    const local = ready ? null : localPreviewRef(id)
    if (local) placeDropped(local, at)
    else pendingDrops.set(id, at)
  })
  applyPendingDrops()
}
// Fertige Uploads: blob:-Vorschauen gegen Server-URLs tauschen (danach wird gespeichert).
store.setLocalUploadResolver((uploadId) => {
  if (!designUploads.readyIds.value.has(uploadId)) return null
  const rec = designUploads.records.value.find((r) => r.id === uploadId)
  return rec ? uploadRefOf(rec) : null
})
watch(designUploads.readyIds, () => { store.swapLocalSources() })
onBeforeUnmount(() => store.setLocalUploadResolver(null))
function applyPendingDrops() {
  if (!pendingDrops.size) return
  for (const rec of designUploads.records.value) {
    const target = pendingDrops.get(rec.id)
    if (!target) continue
    if (rec.upload?.state === 'failed') { pendingDrops.delete(rec.id); continue }
    if (!designUploads.readyIds.value.has(rec.id)) continue
    const ref = uploadRefOf(rec)
    pendingDrops.delete(rec.id)
    if (ref) placeDropped(ref, target)
  }
}
watch(designUploads.readyIds, applyPendingDrops)

/**
 * Aus dem normalen Editor mitgebrachte Bilder (?imageIds=a,b). Nur beim Neuanlegen
 * (kein designId beim Start) – der Reihe nach in leere Fotoplätze, sobald hochgeladen.
 */
const handoffIds = ref<string[]>(designId.value ? [] : String(route.query.imageIds || '').split(',').map((id) => id.trim()).filter(Boolean).slice(0, 20))
function applyHandoff() {
  if (!handoffIds.value.length || !store.activeDoc) return
  const byId = new Map(designUploads.records.value.map((rec) => [rec.id, rec]))
  const rest = [...handoffIds.value]
  while (rest.length) {
    const rec = byId.get(rest[0]!)
    if (rec?.upload?.state === 'failed') { rest.shift(); continue }
    if (!rec || !designUploads.readyIds.value.has(rec.id)) break
    const ref = uploadRefOf(rec)
    rest.shift()
    if (ref) onPick(ref)
  }
  handoffIds.value = rest
  if (!rest.length && route.query.imageIds) {
    const query = { ...route.query }
    delete query.imageIds
    void router.replace({ query })
  }
}
watch([designUploads.readyIds, () => !!store.activeDoc], applyHandoff)
watch(() => [store.projectCode, productId.value, categoryId.value, product.value] as const, ([code, pid, cid, prod]) => {
  if (!code) return
  designUploads.updateContext({ projectCode: code, productId: pid, categoryId: cid, product: prod })
})

function onReplaceImage(layerId: string) {
  replaceTargetId.value = layerId
  openImagesTool()
}

// ---------- Sidebar (controlled) ----------
function onPresetChange(next: { productId?: number | null; size?: any; options?: any[]; quantity?: number; grayscale?: boolean }) {
  if (next.quantity) { quantity.value = next.quantity; preset.value.quantity = next.quantity }
  if (Array.isArray(next.options)) preset.value.options = next.options
  if (typeof next.grayscale === 'boolean') preset.value.grayscale = next.grayscale
  if (next.size) {
    preset.value.size = next.size
    const opt = sizeOptions.value.find((o: any) => String(o.variantId) === String(next.size?.variantId) || String(o.id) === String(next.size?.id))
    const geometry = normalizeGeometry(opt?.geometry ?? next.size.geometry ?? {}, { widthMm: next.size.widthMm, heightMm: next.size.heightMm })
    if (sheetMode.value) {
      if (store.activeMeta && opt) store.activeMeta.sizeId = Number(opt.id)
    } else if (geometry.trimWidthMm > 0 && geometry.trimHeightMm > 0) {
      store.setCanvasSize(canvasFromGeometry(geometry, store.activeDoc?.canvas.background))
      if (store.activeMeta) store.activeMeta.sizeId = opt ? Number(opt.id) : store.activeMeta.sizeId
    }
  }
}

// ---------- Druckfreigabe (Proof) ----------
/**
 * Proof-Render vom Server holen (100 dpi, mit Schnittlinie). Bleibt der Render-Service länger als
 * ~25 s "pending" oder fällt aus, zeigen wir einen Client-Snapshot der Fläche als Fallback; der
 * proofHash kommt in beiden Fällen vom Server (Hash des gespeicherten Stands).
 */
async function runProof(): Promise<boolean> {
  const m = store.activeMeta
  if (!m) return false
  proof.value = { open: true, status: 'loading', imageUrl: '', proofHash: '', warnings: [], error: '' }
  const done = new Promise<boolean>((resolve) => { proofResolve = resolve })
  const startedAt = Date.now()
  let lastHash = ''
  const poll = async (): Promise<void> => {
    if (!proof.value.open) return
    try {
      const res = await requestDesignProof({ editorBaseUrl: cfg.editorBaseUrl, projectCode: store.projectCode, id: m.id })
      lastHash = res.proofHash || lastHash
      proof.value.warnings = res.warnings ?? []
      if (res.status === 'done' && res.proofUrl) {
        proof.value = { ...proof.value, status: 'ready', imageUrl: res.proofUrl, proofHash: lastHash }
        return
      }
      if (res.status === 'failed' || Date.now() - startedAt > 25_000) {
        await fallbackSnapshot(lastHash, res.error || '')
        return
      }
    } catch (e: any) {
      if (Date.now() - startedAt > 25_000 || !e?.status) { await fallbackSnapshot(lastHash, e?.error || e?.message || ''); return }
    }
    if (proof.value.open && proof.value.status === 'loading') setTimeout(() => { void poll() }, 2000)
  }
  const fallbackSnapshot = async (hash: string, err: string) => {
    try {
      const root = document.querySelector('.dp__shell .dr-root') as HTMLElement | null
      const url = root ? await snapshotSurface(root, { widthPx: 900, format: 'jpeg', quality: 0.85 }) : ''
      proof.value = { ...proof.value, status: url ? 'fallback' : 'error', imageUrl: url, proofHash: hash, error: err }
    } catch {
      proof.value = { ...proof.value, status: 'error', proofHash: hash, error: err || 'Vorschau konnte nicht erzeugt werden' }
    }
  }
  void poll()
  return done
}
function onProofConfirm() { proof.value.open = false; proofResolve?.(true); proofResolve = null }
function onProofCancel() { proof.value.open = false; proofResolve?.(false); proofResolve = null }

// ---------- Warenkorb ----------
async function addToCart() {
  const m = store.activeMeta
  if (!m || !store.activeDoc) return
  if (store.blocked) { toast.error('Bitte zuerst die markierten Punkte beheben.'); return }
  if (store.pendingLocalUploads > 0) { toast.info('Einen Moment – Ihre Bilder werden noch hochgeladen.'); return }
  if (sheetMode.value && sheetFilled.value < sheetSlots.value.length) {
    const empty = sheetSlots.value.length - sheetFilled.value
    if (!sheetFilled.value) { toast.error('Bitte zuerst Fotos in die Plätze setzen.'); return }
    if (!window.confirm(`${empty} von ${sheetSlots.value.length} Plätzen sind noch leer und bleiben weiß. Trotzdem in den Warenkorb legen?`)) return
  }
  addingToCart.value = true
  try {
    await store.flushSave()
    if (store.saveState === 'conflict' || store.saveState === 'error') { toast.error(store.saveError || 'Speichern fehlgeschlagen.'); return }
    // Druckfreigabe: Proof ansehen + bestätigen (Pflicht), Hash wandert in die Warenkorb-Position
    const confirmed = await runProof()
    if (!confirmed) return
    const proofHash = proof.value.proofHash || null
    const proofConfirmedAt = new Date().toISOString()
    void requestDesignPreview({ editorBaseUrl: cfg.editorBaseUrl, projectCode: store.projectCode, id: m.id }).catch(() => {})
    const sizeOpt = sizeOptions.value.find((o: any) => Number(o.id) === Number(m.sizeId)) ?? null
    const size = preset.value.size ?? (sizeOpt ? { id: String(sizeOpt.id), variantId: String(sizeOpt.variantId), title: sizeOpt.title, widthMm: store.activeDoc.canvas.widthMm, heightMm: store.activeDoc.canvas.heightMm } : null)
    const selectedVariantIds = [...new Set([Number(size?.variantId || 0), ...preset.value.options.map((o: any) => Number(o.variantId || 0))].filter((n) => n > 0))]
    await cart.addItems({ items: [{
      kind: 'design', type: 'design', itemType: 'design',
      productId: productId.value,
      designDocumentId: m.id,
      editorProjectId: store.projectCode,
      categoryId: categoryId.value,
      productTitle: String((product.value as any)?.title || ''),
      quantity: quantity.value,
      preset: { size, options: preset.value.options, quantity: quantity.value, grayscale: preset.value.grayscale },
      selectedVariantIds,
      proofHash,
      proofConfirmedAt,
      title: String((product.value as any)?.title || 'Gestaltung'),
      meta: [size?.title, ...preset.value.options.map((o: any) => o.value)].filter(Boolean),
    }] })
    toast.success('In den Warenkorb gelegt.')
  } catch (e: any) {
    toast.error(e?.data?.message || e?.message || 'Konnte nicht in den Warenkorb legen.')
  } finally {
    addingToCart.value = false
  }
}

function goToCart() {
  const href = cartHref.value
  if (!href) return
  void store.flushSave().then(refreshThumbnail).finally(() => { window.location.href = href })
}
/** Designer nur zusätzlich angeboten (mode simple + designAvailable) -> Rückweg in den Foto-Editor. */
const canSwitchToPhotoEditor = computed(() => editorConfig.value.mode === 'simple' && editorConfig.value.designAvailable)
function goToPhotoEditor() {
  void store.flushSave().then(refreshThumbnail).finally(() => {
    void router.push({
      path: '/multi',
      query: {
        editorType: 'multi',
        ...(productId.value ? { product: String(productId.value), productId: String(productId.value) } : {}),
        ...(categoryId.value ? { category: String(categoryId.value), categoryId: String(categoryId.value) } : {}),
        ...(store.projectCode ? { projectCode: store.projectCode } : {}),
      },
    })
  })
}
// ---------- Vorschaubild (Client-Snapshot) für „Meine Designs“ + Warenkorb ----------
// Der Render-Service liefert nicht immer ein Vorschaubild; nach dem Speichern und vor einem
// Wechsel wird deshalb ein kleiner Snapshot der aktiven Fläche hochgeladen (nur bei Änderung).
let thumbTimer: ReturnType<typeof setTimeout> | null = null
const thumbHashes = new Map<string, string>()
async function refreshThumbnail() {
  if (thumbTimer) { clearTimeout(thumbTimer); thumbTimer = null }
  const m = store.activeMeta
  const d = store.activeDoc
  if (!m || !d || store.templateMode) return
  const hash = documentHash(d)
  if (thumbHashes.get(m.id) === hash) return
  const root = document.querySelector('.dp__shell .dr-root') as HTMLElement | null
  if (!root) return
  try {
    const url = await snapshotSurface(root, { widthPx: 360, format: 'jpeg', quality: 0.8 })
    if (url && await store.pushThumbnail(url)) thumbHashes.set(m.id, hash)
  } catch { /* Vorschaubild ist optional (Kundenfotos im Objektspeicher sind nicht CORS-lesbar) */ }
}
watch(() => store.saveState, (state) => {
  if (state !== 'saved') return
  if (thumbTimer) clearTimeout(thumbTimer)
  thumbTimer = setTimeout(() => { void refreshThumbnail() }, 4000)
})

// ---------- Meine Designs: wechseln / neu ----------
async function openOtherDesign(target: { id: string; productId: number; categoryId: number }) {
  await store.flushSave().catch(() => {})
  await refreshThumbnail()
  handoffIds.value = []
  if (target.productId > 0) productId.value = target.productId
  const query: Record<string, any> = { ...route.query, designId: target.id }
  delete query.imageIds
  if (target.productId > 0) { query.productId = String(target.productId); delete query.product }
  if (target.categoryId > 0) { query.categoryId = String(target.categoryId); delete query.category }
  // Der designId-Watcher lädt neu.
  await router.replace({ query })
}
async function startNewDesign() {
  await store.flushSave().catch(() => {})
  await refreshThumbnail()
  handoffIds.value = []
  const query: Record<string, any> = { ...route.query }
  delete query.designId
  delete query.imageIds
  await router.replace({ query })
  await boot()
  await nextTick()
  shellRef.value?.openTool?.('templates')
}

// ---------- Name des Designs ----------
const nameDraft = ref('')
const nameSaving = ref(false)
const namePlaceholder = computed(() => `${(product.value as any)?.title || 'Design'} – Namen vergeben`)
watch(() => store.designName, (name) => { if (!nameSaving.value) nameDraft.value = name }, { immediate: true })
async function saveName() {
  const next = nameDraft.value.replace(/\s+/g, ' ').trim()
  if (next === store.designName) { nameDraft.value = next; return }
  nameSaving.value = true
  try {
    await store.rename(next)
    nameDraft.value = store.designName
    if (next) toast.success('Name gespeichert')
  } catch {
    nameDraft.value = store.designName
    toast.error('Der Name konnte nicht gespeichert werden.')
  } finally {
    nameSaving.value = false
  }
}

function goToOverview() {
  void store.flushSave().then(refreshThumbnail).finally(() => { window.location.href = overviewHref.value })
}

function cycleGuides() {
  guides.value = guides.value === 'full' ? 'cut-only' : guides.value === 'cut-only' ? 'off' : 'full'
}
function onPageHide() { void store.flushSave() }

onMounted(() => { void boot(); window.addEventListener('pagehide', onPageHide) })
onBeforeUnmount(() => { stopMercure?.(); if (designUploadsStarted) designUploads.stop(); window.removeEventListener('pagehide', onPageHide); void store.flushSave() })
watch(designId, (id, prev) => { if (id && prev && id !== prev) void boot() })
</script>

<template>
  <div class="dp">
    <ProofDialog
      v-if="proof.open"
      :status="proof.status"
      :image-url="proof.imageUrl"
      :warnings="proof.warnings"
      :error="proof.error"
      :color-hint="editorConfig.colorHintText"
      :geometry="store.geometry"
      @confirm="onProofConfirm"
      @cancel="onProofCancel"
    />

    <div v-if="loading" class="dp__state"><LoaderCircle class="dp__spin" :size="22" /> Editor wird geladen…</div>
    <div v-else-if="loadError" class="dp__state dp__state--err"><TriangleAlert :size="20" /> {{ loadError }}</div>

    <DesignEditorShell v-else-if="store.activeDoc" ref="shellRef" class="dp__shell" :features="features" :fonts="fonts" :palette="palette" @replace-image="onReplaceImage">
      <template #product>
        <Sidebar
          :key="`design-sidebar-${productId}`"
          :product-id="productId || undefined"
          :category="categoryId"
          :show-upload="false"
          :is-multi-mode="false"
          :controlled="true"
          :controlled-size-id="currentSizeId"
          :logo-url="cfg.logoUrl || undefined"
          @preset-change="onPresetChange"
          @add-to-cart="addToCart"
          @price-change="(label: string) => priceLabel = label"
        />
      </template>

      <template #top>
        <header class="dp__top">
          <div class="dp__left">
            <button type="button" class="dp__ghost" title="Zur Übersicht" @click="goToOverview"><ArrowLeft :size="16" /><span>Übersicht</span></button>
            <button v-if="canSwitchToPhotoEditor" type="button" class="dp__ghost" title="Zurück zum normalen Foto-Editor (ohne Vorlagen)" @click="goToPhotoEditor"><ImageIcon :size="16" /><span>Foto-Editor</span></button>
            <div class="dp__title">
              <label class="dp__name" :class="{ 'dp__name--empty': !nameDraft }" :title="store.templateMode ? '' : 'Name des Designs – zum Ändern klicken'">
                <input
                  v-if="!store.templateMode && store.meta"
                  v-model="nameDraft"
                  class="dp__name-input"
                  type="text"
                  maxlength="120"
                  :placeholder="namePlaceholder"
                  aria-label="Name des Designs"
                  :disabled="nameSaving"
                  @keydown.enter.prevent="($event.target as HTMLInputElement).blur()"
                  @keydown.esc.prevent="nameDraft = store.designName; ($event.target as HTMLInputElement).blur()"
                  @blur="saveName"
                />
                <strong v-else>{{ (product as any)?.title || 'Design-Editor' }}</strong>
                <Pencil v-if="!store.templateMode && store.meta" class="dp__name-pen" :size="13" aria-hidden="true" />
              </label>
              <span v-if="!store.templateMode && store.meta && (product as any)?.title" class="dp__format">{{ (product as any).title }}</span>
              <span v-if="store.activeDoc" class="dp__format">{{ store.activeDoc.canvas.widthMm }} × {{ store.activeDoc.canvas.heightMm }} mm<template v-if="store.activeDoc.canvas.bleedMm"> · {{ store.activeDoc.canvas.bleedMm }} mm Beschnitt</template></span>
            </div>
          </div>

          <div class="dp__tools" role="toolbar" aria-label="Ansicht und Bearbeitung">
            <div class="dp__group">
              <button type="button" class="dp__icon" :disabled="!store.canUndo" title="Rückgängig (Strg+Z)" aria-label="Rückgängig" @click="store.undo()"><Undo2 :size="17" /></button>
              <button type="button" class="dp__icon" :disabled="!store.canRedo" title="Wiederholen (Strg+Shift+Z)" aria-label="Wiederholen" @click="store.redo()"><Redo2 :size="17" /></button>
            </div>
            <div class="dp__group">
              <button type="button" class="dp__icon" :class="{ 'dp__icon--on': guides !== 'off' }" :title="guidesTitle" :aria-pressed="guides !== 'off'" aria-label="Produktionsansicht" @click="cycleGuides"><Scissors :size="17" /></button>
              <button type="button" class="dp__icon" :class="{ 'dp__icon--on': productView }" title="Produktansicht (Form ausgeschnitten)" :aria-pressed="productView" aria-label="Produktansicht" @click="productView = !productView"><Eye :size="17" /></button>
            </div>
            <div v-if="store.sibling" class="dp__seg" role="group" aria-label="Seite">
              <button type="button" :class="{ on: store.activeSide === 'front' }" @click="store.activeSide = 'front'">Vorderseite</button>
              <button type="button" :class="{ on: store.activeSide === 'back' }" @click="store.activeSide = 'back'">Rückseite</button>
            </div>
          </div>

          <div class="dp__right">
            <span v-if="saveLabel" class="dp__save" :data-state="store.saveState" aria-live="polite">
              <LoaderCircle v-if="store.saveState === 'saving'" class="dp__spin" :size="14" />
              <CloudOff v-else-if="store.saveState === 'offline'" :size="14" />
              <TriangleAlert v-else-if="store.saveState === 'conflict' || store.saveState === 'error'" :size="14" />
              <Check v-else :size="14" />
              {{ saveLabel }}
            </span>
            <button type="button" class="dp__cartlink" title="Zum Warenkorb" @click="goToCart"><ShoppingCart :size="16" /><span>{{ cart.count }}</span></button>
            <button type="button" class="dp__cart" :disabled="addingToCart || loading || store.blocked || store.pendingLocalUploads > 0" @click="addToCart">
              <LoaderCircle v-if="addingToCart || store.pendingLocalUploads > 0" class="dp__spin" :size="16" />
              <ShoppingCart v-else :size="16" />
              <span>{{ addingToCart ? 'Wird hinzugefügt…' : store.pendingLocalUploads > 0 ? 'Bild wird hochgeladen…' : 'In den Warenkorb' }}</span>
              <span v-if="priceLabel && !addingToCart" class="dp__price">{{ priceLabel }}</span>
            </button>
          </div>
        </header>

        <div v-if="sheetMode && store.activeDoc?.meta.sheet" class="dp__sheetbar" aria-live="polite">
          <span><strong>{{ activeSheet?.name || store.activeDoc.meta.sheet.name || 'Bogen' }}</strong> · {{ sheetFilled }} von {{ sheetSlots.length }} Plätzen belegt</span>
          <span class="dp__sheetbar-hint">Foto auf einen Platz ziehen oder Platz anklicken und Foto wählen · Doppelklick: Ausschnitt verschieben</span>
          <button v-if="selectedUpload && sheetFilled < sheetSlots.length" type="button" @click="fillEmptySlotsWithSelected">Markiertes Foto in alle leeren Plätze</button>
          <button v-else-if="!sheetFilled" type="button" @click="openImagesTool">Fotos wählen</button>
        </div>
        <div v-if="store.draftAvailable" class="dp__banner">
          Es gibt einen neueren lokalen Entwurf von {{ new Date(store.draftAvailable.savedAt).toLocaleString('de-DE') }}.
          <button type="button" @click="store.restoreDraft()">Wiederherstellen</button>
          <button type="button" @click="store.discardDraft()">Verwerfen</button>
        </div>
        <div v-if="store.saveState === 'conflict'" class="dp__banner dp__banner--warn">
          Dieses Design wurde in einem anderen Fenster geändert.
          <button type="button" @click="store.adoptServerVersion()">Anderen Stand übernehmen</button>
          <button type="button" @click="store.overwriteServerVersion()">Meinen Stand behalten</button>
        </div>
        <div v-if="store.saveState === 'error' && store.saveError" class="dp__banner dp__banner--warn">{{ store.saveError }}</div>
      </template>

      <template #stage>
        <Stage :doc="store.activeDoc" :geometry="store.geometry" :guides="guides" :product-view="productView" :snap-enabled="editorConfig.canvas.snapEnabled" @drop-upload="onDropUpload" @drop-files="onDropFiles" />
      </template>
      <template #images>
        <ImagesPanel :target-layer-id="replaceTargetId" @pick="onPick" @auto-fill="store.autoFill($event)" />
      </template>
      <template #text><TextPanel :fonts="fonts" /></template>
      <template #stickers><StickersPanel :product-id="productId" /></template>
      <template #templates><TemplatesPanel :product-id="productId" :category-id="categoryId" :size-id="currentSizeId" /></template>
      <template #layouts><LayoutsPanel /></template>
      <template #designs><MyDesignsPanel :product-titles="productTitles" @open="openOtherDesign" @new="startNewDesign" /></template>
      <template #background><BackgroundPanel :palette="palette" :allow-free-color="features.freeColor" :product-id="productId" /></template>
    </DesignEditorShell>
  </div>
</template>

<style scoped>
.dp { display: flex; flex-direction: column; height: 100vh; height: 100dvh; min-height: 0; background: #fff; color: #0f172a; }
.dp__shell { flex: 1; min-height: 0; }
.dp__top { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 56px; padding: 8px 16px; border-bottom: 1px solid rgba(15, 23, 42, 0.08); background: #fff; }
.dp__left { flex: 1 1 auto; }
.dp__right { flex: 0 0 auto; }
.dp__left, .dp__right, .dp__tools { display: flex; align-items: center; gap: 10px; min-width: 0; }
.dp__tools { gap: 8px; }
.dp__title { display: flex; flex-direction: column; min-width: 0; line-height: 1.25; }
.dp__title strong { font-size: 15px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dp__format { font-size: 12px; color: rgba(15, 23, 42, 0.58); white-space: nowrap; }
.dp__name { position: relative; display: inline-flex; align-items: center; gap: 4px; min-width: 0; max-width: 320px; cursor: text; }
.dp__name strong { font-size: 15px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dp__name-input { min-width: 0; width: 100%; height: 26px; margin-left: -6px; padding: 0 22px 0 6px; border: 1px solid transparent; border-radius: 4px; background: transparent; color: #0f172a; font: inherit; font-size: 15px; font-weight: 700; text-overflow: ellipsis; }
.dp__name-input::placeholder { color: rgba(15, 23, 42, 0.45); font-weight: 600; }
.dp__name:hover .dp__name-input { border-color: rgba(15, 23, 42, 0.16); background: #fff; }
.dp__name-input:focus { outline: none; border-color: var(--pfe-color-accent, #e30b17); background: #fff; box-shadow: 0 0 0 3px rgba(227, 11, 23, 0.12); }
.dp__name-pen { position: absolute; right: 6px; color: rgba(15, 23, 42, 0.4); pointer-events: none; }
.dp__name-input:focus + .dp__name-pen { display: none; }
.dp__ghost { display: inline-flex; align-items: center; gap: 6px; height: 36px; padding: 0 12px; border: 1px solid rgba(15, 23, 42, 0.16); border-radius: 6px; background: #fff; color: #0f172a; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; }
.dp__ghost:hover { border-color: rgba(15, 23, 42, 0.32); }
.dp__group { display: inline-flex; padding: 2px; border: 1px solid rgba(15, 23, 42, 0.12); border-radius: 8px; background: #fff; }
.dp__icon { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 30px; border: 0; border-radius: 6px; background: transparent; color: rgba(15, 23, 42, 0.72); cursor: pointer; }
.dp__icon:hover:not(:disabled) { background: rgba(15, 23, 42, 0.06); color: #0f172a; }
.dp__icon:disabled { opacity: 0.35; cursor: default; }
.dp__icon--on { background: rgba(227, 11, 23, 0.08); color: var(--pfe-color-accent, #e30b17); }
.dp__seg { display: inline-flex; padding: 2px; border: 1px solid rgba(15, 23, 42, 0.12); border-radius: 8px; background: #fff; }
.dp__seg button { height: 30px; padding: 0 12px; border: 0; border-radius: 6px; background: transparent; font: inherit; font-size: 13px; cursor: pointer; color: rgba(15, 23, 42, 0.72); }
.dp__seg button.on { background: #0f172a; color: #fff; }
.dp__save { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; color: rgba(15, 23, 42, 0.58); white-space: nowrap; }
.dp__save[data-state="conflict"], .dp__save[data-state="error"] { color: var(--pfe-color-accent, #e30b17); }
.dp__cartlink { display: inline-flex; align-items: center; gap: 5px; height: 36px; padding: 0 10px; border: 1px solid rgba(15, 23, 42, 0.16); border-radius: 6px; background: #fff; color: #0f172a; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; }
.dp__cart { display: inline-flex; align-items: center; gap: 8px; height: 36px; padding: 0 14px; border: 0; border-radius: 6px; background: var(--pfe-color-accent, #e30b17); color: #fff; font: inherit; font-size: 14px; font-weight: 700; cursor: pointer; white-space: nowrap; }
.dp__cart:hover:not(:disabled) { filter: brightness(0.94); }
.dp__cart:disabled { opacity: 0.5; cursor: default; }
.dp__price { padding-left: 8px; border-left: 1px solid rgba(255, 255, 255, 0.45); }
.dp__banner { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; padding: 8px 16px; font-size: 13px; background: #eef2ff; }
.dp__banner--warn { background: #fdecea; }
.dp__banner button { padding: 4px 8px; border: 1px solid rgba(15, 23, 42, 0.2); border-radius: 6px; background: #fff; cursor: pointer; font: inherit; }
.dp__state { display: flex; align-items: center; justify-content: center; gap: 10px; padding: 64px 16px; color: rgba(15, 23, 42, 0.62); }
.dp__state--err { color: var(--pfe-color-accent, #e30b17); }
.dp__spin { animation: dp-spin 0.9s linear infinite; }
@keyframes dp-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .dp__spin { animation: none; } }
/* Kopfzeile richtet sich nach der Breite der Mitte (Eigenschaften-Spalte nimmt Platz weg) */
@container dmain (max-width: 980px) {
  .dp__top { padding: 8px 12px; gap: 8px; }
  .dp__ghost span, .dp__save { display: none; }
  .dp__ghost { padding: 0 10px; }
}
@container dmain (max-width: 780px) {
  .dp__format, .dp__price { display: none; }
}
@container dmain (max-width: 620px) {
  .dp__top { flex-wrap: wrap; }
  .dp__tools { order: 3; width: 100%; justify-content: space-between; }
  .dp__cart span:not(.dp__price) { display: none; }
}
.dp__sheetbar { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 14px; padding: 7px 14px; border-bottom: 1px solid var(--pfe-color-border, rgba(15, 23, 42, 0.1)); background: #f7fafc; font-size: 12.5px; }
.dp__sheetbar-hint { color: var(--pfe-color-muted, rgba(15, 23, 42, 0.6)); }
.dp__sheetbar button { margin-left: auto; padding: 4px 10px; border: 1px solid var(--pfe-color-border, rgba(15, 23, 42, 0.18)); border-radius: 999px; background: #fff; font: inherit; font-size: 12px; cursor: pointer; }
.dp__sheetbar button:hover { border-color: var(--pfe-color-accent, #e30b17); }
</style>
